import { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AppLayout from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  CalendarDays, Clock, Check, Phone, X, Info,
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, RotateCcw,
  Frown, Annoyed, Meh, Smile, Laugh,
} from 'lucide-react';
import { visiteProgrammate, profiloPaziente } from '@/data/mockPatientData';

const statoVisita = {
  confermata: { label: 'Confermata', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  'da-confermare': { label: 'Da confermare', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  conclusa: { label: 'Conclusa', bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
};

/* ══════ FACCINE ══════ */
const faccine = [
  { key: 'molto-insoddisfatto', label: 'Molto insoddisfatto', color: '#DC2626', bgColor: '#FEE2E2' },
  { key: 'insoddisfatto', label: 'Insoddisfatto', color: '#EA580C', bgColor: '#FFF7ED' },
  { key: 'neutro', label: 'Neutro', color: '#CA8A04', bgColor: '#FEFCE8' },
  { key: 'soddisfatto', label: 'Soddisfatto', color: '#65A30D', bgColor: '#F7FEE7' },
  { key: 'molto-soddisfatto', label: 'Molto soddisfatto', color: '#15803D', bgColor: '#F0FDF4' },
];

const FACE_ICONS = {
  'molto-insoddisfatto': Frown,
  'insoddisfatto': Annoyed,
  'neutro': Meh,
  'soddisfatto': Smile,
  'molto-soddisfatto': Laugh,
};

function FaceIcon({ type, size = 48 }) {
  const face = faccine.find(f => f.key === type);
  const c = face?.color || '#6B7280';
  const Icon = FACE_ICONS[type] || Meh;
  return <Icon width={size} height={size} color={c} strokeWidth={1.8} />;
}

/* ══════ Satisfaction Rating ══════ */
function SatisfactionRating({ visitaId, onRate }) {
  const [rated, setRated] = useState(null);

  if (rated) {
    const f = faccine.find(x => x.key === rated);
    return (
      <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="flex items-center justify-center py-3" data-testid={`feedback-done-${visitaId}`}>
        <div className="flex flex-col items-center gap-1.5 px-5 py-3 rounded-2xl ring-2" style={{ backgroundColor: f?.bgColor, '--tw-ring-color': f?.color }}>
          <FaceIcon type={rated} size={56} />
          <span className="text-xs font-bold" style={{ color: f?.color }}>{f?.label}</span>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-2.5" data-testid={`feedback-rating-${visitaId}`}>
      <p className="text-sm font-bold text-foreground text-center">Come valuti la visita?</p>
      <div className="flex justify-center gap-2">
        {faccine.map((f) => (
          <button
            key={f.key}
            onClick={() => { setRated(f.key); if (onRate) onRate(f.key); }}
            className="flex flex-col items-center gap-1 px-2.5 py-2.5 rounded-2xl border-2 border-transparent transition-all active:scale-95 hover:scale-105"
            style={{ backgroundColor: f.bgColor }}
            data-testid={`rating-btn-${f.key}-${visitaId}`}
          >
            <FaceIcon type={f.key} size={40} />
            <span className="text-[9px] font-bold leading-tight text-center max-w-[60px]" style={{ color: f.color }}>{f.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ══════ Utility date ══════ */
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const MESI_SHORT = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];
const GIORNI_SHORT = ['Dom','Lun','Mar','Mer','Gio','Ven','Sab'];

function getDayLabel(d, today) {
  if (sameDay(d, today)) return 'Oggi';
  const diff = Math.round((d.getTime() - today.getTime()) / (86400000));
  if (diff === -1) return 'Ieri';
  if (diff === 1) return 'Domani';
  return `${GIORNI_SHORT[d.getDay()]} ${d.getDate()} ${MESI_SHORT[d.getMonth()]}`;
}

/* ══════ Date Selector ══════ */
function DateSelectorHorizontal({ selectedDate, onDateChange, onToggleCalendar }) {
  const today = useMemo(() => { const t = new Date(); t.setHours(0,0,0,0); return t; }, []);
  const prev = new Date(selectedDate); prev.setDate(prev.getDate() - 1);
  const next = new Date(selectedDate); next.setDate(next.getDate() + 1);
  const days = [
    { date: prev, label: getDayLabel(prev, today) },
    { date: new Date(selectedDate), label: getDayLabel(selectedDate, today) },
    { date: next, label: getDayLabel(next, today) },
  ];
  const isToday = sameDay(selectedDate, today);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-center gap-1.5" data-testid="date-selector">
        <button onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() - 1); onDateChange(d); }} className="w-10 h-10 rounded-2xl border border-border/30 flex items-center justify-center hover:bg-muted/30 transition-colors active:scale-95" data-testid="date-prev">
          <ChevronLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <div className="flex gap-1 p-1 bg-muted/20 rounded-2xl">
          {days.map((d, i) => (
            <button key={i} onClick={() => onDateChange(d.date)} className={`px-3.5 py-2.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${i === 1 ? 'bg-card text-foreground shadow-md' : 'text-muted-foreground hover:text-foreground'}`} data-testid={`date-btn-${i}`}>
              {d.label}
            </button>
          ))}
        </div>
        <button onClick={() => { const d = new Date(selectedDate); d.setDate(d.getDate() + 1); onDateChange(d); }} className="w-10 h-10 rounded-2xl border border-border/30 flex items-center justify-center hover:bg-muted/30 transition-colors active:scale-95" data-testid="date-next">
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </button>
        <button onClick={onToggleCalendar} className="w-10 h-10 rounded-2xl flex items-center justify-center transition-colors active:scale-95 bg-sky-50 border border-sky-200 hover:bg-sky-100" data-testid="btn-calendario-esteso">
          <CalendarIcon className="w-5 h-5 text-sky-600" />
        </button>
      </div>
      {!isToday && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="flex justify-center">
          <button onClick={() => onDateChange(today)} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold text-sky-600 hover:text-sky-700 hover:bg-sky-50 rounded-full transition-colors" data-testid="btn-torna-oggi">
            <RotateCcw className="w-3.5 h-3.5" /> Torna a Oggi
          </button>
        </motion.div>
      )}
    </div>
  );
}

/* ══════ PAGINA AGENDA ══════ */
export default function PatientAgenda() {
  const today = useMemo(() => { const t = new Date(); t.setHours(0,0,0,0); return t; }, []);
  const [selectedDate, setSelectedDate] = useState(today);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedVisita, setSelectedVisita] = useState(null);
  const calendarRef = useRef(null);

  useEffect(() => {
    if (!showCalendar) return;
    const handleClick = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) setShowCalendar(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCalendar]);

  const visiteGiorno = useMemo(() => {
    return visiteProgrammate.filter(v => v.dateObj && sameDay(v.dateObj, selectedDate));
  }, [selectedDate]);

  return (
    <AppLayout role="paziente">
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="patient-agenda">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-sky-50 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-sky-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="page-title">La mia Agenda</h1>
            <p className="text-sm text-muted-foreground">I tuoi prossimi appuntamenti — {profiloPaziente.nome} {profiloPaziente.cognome}</p>
          </div>
        </div>

        {/* Date Selector + Calendar Dropdown */}
        <div className="relative">
          <DateSelectorHorizontal
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onToggleCalendar={() => setShowCalendar(prev => !prev)}
          />
          <AnimatePresence>
            {showCalendar && (
              <motion.div ref={calendarRef} initial={{ opacity: 0, y: -8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.95 }} transition={{ duration: 0.15 }} className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50" data-testid="calendar-picker">
                <Card className="border border-border/30 shadow-xl rounded-3xl">
                  <CardContent className="p-4">
                    <Calendar mode="single" selected={selectedDate} onSelect={(date) => { if (date) { setSelectedDate(date); setShowCalendar(false); } }} className="rounded-2xl" />
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Lista visite */}
        {visiteGiorno.length === 0 ? (
          <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.04)] rounded-3xl" data-testid="no-visite">
            <CardContent className="p-8 text-center">
              <CalendarDays className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm font-semibold text-muted-foreground">Nessuna visita programmata per questo giorno</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-5">
            {visiteGiorno.map((v, i) => {
              const st = statoVisita[v.stato] || statoVisita.confermata;
              const isVisitaPast = v.stato === 'conclusa';
              return (
                <motion.div key={v.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card className="border-0 shadow-[0_1px_6px_rgba(0,0,0,0.05)] rounded-3xl" data-testid={`visita-${v.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm ${isVisitaPast ? 'bg-gradient-to-br from-slate-300 to-slate-400' : 'bg-gradient-to-br from-sky-400 to-teal-400'}`}>
                          {v.avatar}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-base font-bold text-foreground mt-0.5">{v.tipo}</p>
                          <p className="text-sm text-muted-foreground">{v.operatore}</p>
                          <div className="flex items-center gap-1.5 mt-2">
                            <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                            <span className="text-sm font-semibold text-foreground">{v.fascia}</span>
                          </div>
                        </div>
                        <Badge className={`rounded-full px-3 py-1 border-0 text-xs font-bold flex-shrink-0 ${st.bg} ${st.text}`} data-testid={`stato-${v.id}`}>
                          <div className={`w-2 h-2 rounded-full mr-1.5 ${st.dot}`} />{st.label}
                        </Badge>
                      </div>
                      <div className="mt-4 pt-3 border-t border-border/15">
                        {isVisitaPast ? (
                          <SatisfactionRating visitaId={v.id} />
                        ) : (
                          <button className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5" onClick={() => { setSelectedVisita(v); setShowModal(true); }} data-testid={`btn-sposta-${v.id}`}>
                            <Phone className="w-3.5 h-3.5" />Sposta / Cancella Appuntamento
                          </button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Modale Sposta */}
        <AnimatePresence>
          {showModal && selectedVisita && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowModal(false)} data-testid="sposta-modal-overlay">
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="bg-card rounded-3xl shadow-2xl max-w-sm w-full overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="sposta-modal-panel">
                <div className="p-7 flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-5">
                    <Info className="w-7 h-7 text-amber-500" />
                  </div>
                  <h2 className="text-lg font-bold text-foreground">Spostamento Appuntamento</h2>
                  <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                    Per spostare o cancellare la visita di <strong>{selectedVisita.tipo}</strong> del <strong>{selectedVisita.giorno}</strong>, contatta la segreteria ADI al numero:
                  </p>
                  <div className="mt-4 px-5 py-3 rounded-2xl bg-sky-50 border border-sky-100">
                    <p className="text-lg font-bold text-sky-700">06 7730 5200</p>
                    <p className="text-[10px] text-sky-500 mt-0.5">Lun-Ven 08:00 — 16:00</p>
                  </div>
                </div>
                <div className="px-6 pb-6">
                  <Button className="w-full h-12 rounded-2xl gap-2 text-base" onClick={() => setShowModal(false)} data-testid="sposta-modal-close">
                    <Check className="w-5 h-5" />Ho capito
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AppLayout>
  );
}
